import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { zohoAPI } from '@/lib/api';
import { toast } from 'sonner';
import { LayoutDashboard, Loader2, FlaskConical } from 'lucide-react';
import ZohoDashboardStats from '@/components/zoho/ZohoDashboardStats';
import ZohoDashboardFormCards from '@/components/zoho/ZohoDashboardFormCards';
import ZohoDashboardRecent from '@/components/zoho/ZohoDashboardRecent';
import ZohoDashboardActivity from '@/components/zoho/ZohoDashboardActivity';

export default function ZohoDashboardPage() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [excludeTest, setExcludeTest] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, recentRes, activityRes] = await Promise.all([
        zohoAPI.getDashboardStats({ exclude_test: excludeTest }),
        zohoAPI.getDashboardRecent({ exclude_test: excludeTest, limit: 15 }),
        zohoAPI.getDashboardActivity({ limit: 15 }),
      ]);
      setStats(statsRes.data);
      setRecent(recentRes.data.submissions || []);
      setActivity(activityRes.data.activities || []);
    } catch (e) {
      toast.error('Failed to load dashboard data');
    }
    setLoading(false);
  }, [excludeTest]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="zoho-dashboard-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <LayoutDashboard className="h-6 w-6" />
              Overview
            </h1>
            <p className="text-muted-foreground text-sm mt-0.5">
              Sales & expense data at a glance
            </p>
          </div>
          <Button
            variant={excludeTest ? 'default' : 'outline'}
            size="sm"
            onClick={() => setExcludeTest(prev => !prev)}
            data-testid="exclude-test-toggle"
            className="gap-2"
          >
            <FlaskConical className="h-4 w-4" />
            Excl. Test Data
          </Button>
        </div>

        {/* Stats Cards */}
        <ZohoDashboardStats stats={stats} />

        {/* Form Cards */}
        <ZohoDashboardFormCards forms={stats?.forms || []} />

        {/* Recent + Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <ZohoDashboardRecent submissions={recent} />
          </div>
          <div>
            <ZohoDashboardActivity activities={activity} />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
